import React from 'react';

import Image from 'next/image';
import { useRouter } from 'next/router';

import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';

import { Button } from 'components/Feature/Custom/Button';
import { getPdfPath } from 'components/Shared/FileDetailComponent/FileDetailComponent';

import { extractRelativePath } from 'components/Shared/DomainSplit';

import MediaQuery from 'react-responsive';

import { TemplateDownloadIcon } from './TemplateDownloadIcon';
import { submitFileDownload } from 'src/util/CommonApis';

type TitleDescriptionButton = TitleDescriptionImageFields['Buttons'][number];

function normalizeButtons(buttons: TitleDescriptionImageFields['Buttons'] | null | undefined): TitleDescriptionButton[] {
    if (!buttons) return [];
    if (Array.isArray(buttons)) return buttons.filter(Boolean);
    return [];
}

function normalizeMediaPath(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return '';

    const dashIndex = trimmed.indexOf('/-/');
    if (dashIndex !== -1) {
        return trimmed.substring(dashIndex);
    }

    return getPdfPath(trimmed);
}

function prefixSitecoreHost(path: string): string {
    if (!path.startsWith('/')) return path;
    const host = (process.env.NEXT_PUBLIC_SITECORE_API_HOST || '').replace(/\/$/, '');
    return host ? `${host}${path}` : path;
}

/** Browser-reachable download URL (CD host), not localhost-relative /-/media. */
function resolveDownloadHref(rawSrc: string): string {
    const trimmed = rawSrc.trim();
    if (!trimmed) return '';

    if (/^https?:\/\//i.test(trimmed)) {
        const extracted = extractRelativePath(trimmed);
        if (extracted && /^https?:\/\//i.test(extracted)) {
            return extracted;
        }
        if (extracted?.startsWith('/')) {
            return prefixSitecoreHost(extracted);
        }
        return extracted || trimmed;
    }

    return prefixSitecoreHost(normalizeMediaPath(trimmed));
}

function resolveButtonRawFileSrc(btn: TitleDescriptionButton): string {
    const fileSrc = btn?.fields?.File?.value?.src?.trim();
    if (fileSrc) return fileSrc;

    const btnUrl = btn?.fields?.BtnUrl?.value;
    if (!btnUrl) return '';

    return (
        btn?.fields?.isFile?.value
            ? (btnUrl.url || btnUrl.href)
            : (btnUrl.href || btnUrl.url)
    ).trim();
}

function resolveButtonFileName(btn: TitleDescriptionButton, fileSrc: string): string {
    const fileValue = btn?.fields?.File?.value;
    const fileFieldName = (fileValue?.name || fileValue?.title || '').trim();
    const fileExtension = (fileValue?.extension || '').trim();

    if (fileFieldName) {
        if (fileExtension && !fileFieldName.toLowerCase().endsWith(`.${fileExtension.toLowerCase()}`)) {
            return `${fileFieldName}.${fileExtension}`;
        }
        return fileFieldName;
    }

    return fileSrc.split('/').pop()?.split('?')[0] || '';
}

function isRenderableButton(btn: TitleDescriptionButton): boolean {
    return Boolean(btn?.fields?.BtnText?.value?.trim());
}

type TitleDescriptionImageProps = {
    SectionId: string;
    fields: TitleDescriptionImageFields;
    DescriptionFontSize: string;
    locale: string;
}

function TitleDescriptionImage1(props: TitleDescriptionImageProps) {
    const router = useRouter();
    const buttons = normalizeButtons(props?.fields?.Buttons);
    const renderableButtons = buttons.filter(isRenderableButton);

    const handleFileDownloadClick = (
        event: React.MouseEvent,
        fileName: string,
    ) => {
        event.stopPropagation();
        void submitFileDownload({
            Language: props.locale || router.locale || 'en',
            FileName: fileName,
            PageName: router.asPath,
            Commision: 'Moc',
        }).catch(() => {
            /* allow native anchor download */
        });
    };

    if (
        !props?.fields?.Title?.value &&
        !props?.fields?.Image?.value?.src &&
        !props?.fields?.Description?.value &&
        renderableButtons.length === 0
    ) return <></>;

    const firstButtonWithLinkValue = renderableButtons[0];

    return (
        <section
            id={props?.SectionId}
            className={
                'py-10 ' + (
                    (!props?.fields?.isLightBgColor?.value &&
                        !props?.fields?.isDarkBgColor?.value)
                        ? "bg-transparent"
                        : (props?.fields?.isLightBgColor?.value
                            ? "bg-neutral-100"
                            : props?.fields?.isDarkBgColor?.value
                                ? "bg-neutral-300"
                                : '')
                )
            }
        >
            <div className="responsive flex flex-col md:flex-row items-stretch gap-6 w-full">
                {
                    (
                        props?.fields?.Title?.value ||
                        props?.fields?.Image?.value?.src ||
                        props?.fields?.Description?.value ||
                        renderableButtons.length > 0
                    ) && (
                        <div className='flex-1 flex flex-col items-start gap-6 md:gap-8'>
                            {
                                props?.fields?.Title?.value && (
                                    <h1
                                        id={'section-title-' + props?.SectionId}
                                        className=' animate-top-dga text-neutral-800 text-start text-3xl font-bold'
                                    >
                                        {props?.fields?.Title?.value}
                                    </h1>
                                )
                            }

                            {
                                props?.fields?.Image?.value?.src && (
                                    <MediaQuery maxWidth={767.98}>
                                        <div className='relative min-h-[160px] h-full w-full p-4 pb-0 md:!p-0 overflow-hidden'>
                                            <Image
                                                src={extractRelativePath(props?.fields?.Image?.value?.src)}
                                                alt={(props?.fields?.Image?.value?.alt ?? "").trim() || (props?.fields?.Title?.value ?? "").trim() || "Section image"}
                                                width={366}
                                                height={160}
                                                layout='responsive'
                                                objectFit='fill'
                                                className=' animate-top-dga rounded-lg !min-w-full'
                                            />
                                        </div>
                                    </MediaQuery>
                                )
                            }

                            {
                                props?.fields?.Description?.value && (
                                    <RichText
                                        id={'section-desc-' + props?.SectionId}
                                        field={props?.fields?.Description}
                                        className=' animate-top-dga overflow-hidden text-neutral-800 text-lg font-normal w-full px-4 md:!px-0 break-words'
                                    />
                                )
                            }

                            {
                                renderableButtons.length > 0 && (
                                    <div className='animate-top-dga  overflow-hidden flex flex-wrap w-full justify-start items-center gap-2 md:gap-4 p-4 pt-0 md:!p-0 mt-auto'>
                                        {renderableButtons.map((btn: TitleDescriptionButton, index: number) => {
                                            const isFileDownload = Boolean(btn?.fields?.isFile?.value);
                                            const rawFileSrc = isFileDownload ? resolveButtonRawFileSrc(btn) : '';
                                            const downloadHref = rawFileSrc ? resolveDownloadHref(rawFileSrc) : '';
                                            const fileName = rawFileSrc ? resolveButtonFileName(btn, rawFileSrc) : '';

                                            return (
                                                <React.Fragment key={btn?.id || index}>
                                                    {
                                                        isFileDownload ? (
                                                            <a
                                                                href={downloadHref || '#'}
                                                                download={fileName || undefined}
                                                                target="_self"
                                                                rel="noopener noreferrer"
                                                                onClick={(event) => {
                                                                    if (!downloadHref) {
                                                                        event.preventDefault();
                                                                        return;
                                                                    }
                                                                    handleFileDownloadClick(event, fileName);
                                                                }}
                                                                className={`inline-flex items-center gap-2 ${btn?.id === firstButtonWithLinkValue?.id ? 'dga-primary-button' : 'dga-secondary-button'}`}
                                                            >
                                                                {btn?.fields?.BtnText?.value}
                                                                <TemplateDownloadIcon
                                                                    variant={btn?.id === firstButtonWithLinkValue?.id ? "white" : "grey"}
                                                                    size={20}
                                                                />
                                                            </a>
                                                        ) : (
                                                            <Button
                                                                text={btn?.fields?.BtnText?.value}
                                                                redirect={btn?.fields?.BtnUrl?.value?.href || btn?.fields?.BtnUrl?.value?.url}
                                                                target={btn?.fields?.BtnUrl?.value?.target}
                                                                linktype={btn?.fields?.BtnUrl?.value?.linktype}
                                                                classNames={`${btn?.id === firstButtonWithLinkValue?.id ? 'dga-primary-button' : 'dga-secondary-button'} !border-none !px-4 !py-2 !font-medium !text-base`}
                                                            />
                                                        )
                                                    }
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                )
                            }
                        </div>
                    )
                }

                {
                    props?.fields?.Image?.value?.src && (
                        <MediaQuery minWidth={768}>
                            <div className=' overflow flex-1 relative md:min-h-max md:h-auto w-full p-4 rounded-2xl overflow-hidden'>
                                <Image
                                    src={extractRelativePath(props?.fields?.Image?.value?.src)}
                                    alt={(props?.fields?.Image?.value?.alt ?? "").trim() || (props?.fields?.Title?.value ?? "").trim() || "Section image"}
                                    width={605}
                                    height={362}
                                    layout="fill"
                                    objectFit="cover"
                                    className='rounded-lg min-w-full min-h-full animate-top-dga'
                                />
                            </div>
                        </MediaQuery>
                    )
                }
            </div>
        </section>

    )
}

export default React.memo(TitleDescriptionImage1);

export type TitleDescriptionCom = {
    displayName: string;
    fields: TitleDescriptionImageFields
}

export type TitleDescriptionImageFields = {
    SectionTemplate: {
        displayName: string;
        fields: {
            Title: {
                value: string;
            }
        }
    },
    Description: {
        value: string;
    }
    Buttons:
    {
        fields: {
            BtnText: {
                value: string;
            },
            BtnUrl: {
                value: {
                    href: string;
                    linktype: string;
                    url: string;
                    anchor: string;
                    target: string;
                }
            },
            File: {
                value: {
                    src: string;
                    name: string;
                    title: string;
                    extension: string;
                    mimeType: string;
                    size: string;
                }
            },
            isFile: {
                value: boolean;
            }
        }
        id: string;
    }[],
    Pattern: {
        value: {
            src: string;
            alt: string;
            width: string;
            height: string;
        }
    },
    Title: {
        value: string;
    },
    Image: {
        value: {
            src: string;
            alt: string;
            width: string;
            height: string;
        }
    }
    isScrollAllowed: {
        value: boolean;
    },
    ScrollToText: {
        value: string;
    },
    isLightBgColor: {
        value: string;
    },
    isDarkBgColor: {
        value: string;
    }

}